import React from 'react';
import { API_URL } from '../../utils/api';

/* ── Display Name ─────────────────────────────── */
export const getDisplayName = (variant, allVariants) => {
  const attr = variant.attribute || '';
  const size = variant.size || '';

  const attrCount = allVariants.filter(v => v.attribute === attr).length;
  const isGeneric = attrCount > 3;

  if (!isGeneric && attr) return attr;

  if (variant.image_url) {
    const filename = variant.image_url.split('/').pop() || '';

    const cleaned = filename
      .replace(/\.\w+$/, '')
      .replace(/^\d+_/, '');

    const dashParts = cleaned.split(/_-_/);

    if (dashParts.length >= 2) {
      const shadePart = dashParts[dashParts.length - 1]
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (shadePart && shadePart.length > 1) return shadePart;
    }
  }

  if (attr && attr !== size) return `${attr}${size ? ` (${size})` : ''}`;
  if (size) return size;

  return variant.sku || 'Option';
};

/* ── Variant Card ─────────────────────────────── */
export const VariantCard = ({
  variant,
  isSelected,
  onSelect,
  onImageClick, // thay cho onHover/onHoverEnd
  getImageUrl,
  fallbackImage,
  displayName,
  colorInfo
}) => {
  const imgSrc =
    getImageUrl(variant.image_url) ||
    getImageUrl(variant.hero_image) ||
    fallbackImage;

  const isOOS = variant.inventory_qty <= 0;

  return (
    <button
      className={`variant-card ${isSelected ? 'selected' : ''} ${isOOS ? 'oos' : ''}`}
      onClick={() => !isOOS && onSelect(variant)}
      disabled={isOOS}
      title={displayName}
    >
      {/* IMAGE */}
      <div 
        className="variant-card-img"
        onClick={(e) => {
          e.stopPropagation();
          if (!isOOS) onImageClick?.(variant);
        }}
      >
        {imgSrc ? (
          <img src={imgSrc} alt={displayName} loading="lazy" />
        ) : (
          <div className="variant-card-noimg">?</div>
        )}

        {colorInfo && (
          <div
            className="variant-color-bar"
            style={{
              backgroundColor: colorInfo.value,
              color: getContrastColor(colorInfo.value)
            }}
          >
            <div className="variant-color-code">
              {colorInfo.code}
            </div>
          </div>
        )}
      </div>

      {/* INFO */}
      <div className="variant-card-info">
        <span className="variant-card-name">{displayName}</span>
        <span className="variant-card-price">
          ${parseFloat(variant.retail_price).toFixed(2)}
        </span>
      </div>

      {isOOS && <span className="variant-card-oos">Sold out</span>}
      {isSelected && <span className="variant-card-check">✓</span>}
    </button>
  );
};

const getContrastColor = (hex) => {
  if (!hex) return '#fff';

  const c = hex.replace('#', '');

  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);

  // perceived brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 160 ? '#000' : '#fff';
};

/* ── Image Loader ─────────────────────────────── */
export const useImageLoader = () => {
  return React.useCallback((imgPath) => {
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;

    const baseUrl = API_URL.replace('/api', '');

    if (imgPath.startsWith('/uploads/')) {
      return `${baseUrl}${imgPath}`;
    }

    return `${baseUrl}/api/images/${imgPath}`;
  }, []);
};